// Utility functions for managing logo in localStorage

const LOGO_STORAGE_KEY = 'teklif_company_logo';

/**
 * Save logo to localStorage as base64 string
 * @param {string} base64String - Base64 encoded image string
 */
export const saveLogoToStorage = (base64String) => {
  try {
    localStorage.setItem(LOGO_STORAGE_KEY, base64String);
    return true;
  } catch (error) {
    console.error('Error saving logo to localStorage:', error);
    // Handle quota exceeded error
    if (error.name === 'QuotaExceededError') {
      alert('Storage quota exceeded. Please use a smaller image.');
    }
    return false;
  }
};

/**
 * Get logo from localStorage
 * @returns {string|null} Base64 encoded image string or null
 */
export const getLogoFromStorage = () => {
  try {
    return localStorage.getItem(LOGO_STORAGE_KEY);
  } catch (error) {
    console.error('Error getting logo from localStorage:', error);
    return null;
  }
};

/**
 * Remove logo from localStorage
 */
export const removeLogoFromStorage = () => {
  try {
    localStorage.removeItem(LOGO_STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error removing logo from localStorage:', error);
    return false;
  }
};

/**
 * Convert file to base64 string
 * @param {File} file - Image file
 * @returns {Promise<string>} Base64 encoded string
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Validate image file
 * @param {File} file - Image file to validate
 * @returns {Object} { valid: boolean, error?: string }
 */
export const validateImageFile = (file) => {
  // Check file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.'
    };
  }

  // Check file size (max 2MB)
  const maxSize = 2 * 1024 * 1024; // 2MB in bytes
  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'File size too large. Please upload an image smaller than 2MB.'
    };
  }

  return { valid: true };
};
